// React imports
import React from 'react';

// Third-party imports
import clsx from 'clsx';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// UI Components
import Button from 'components/UI/V3/Button/Button';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';

// Styles
import styles from './ItemSelectionDrawer.module.scss';

export interface ItemDisplayConfig<T> {
    /** Function to get the item's title */
    getTitle: (item: T) => string;
    /** Function to get the item's subtitle/description */
    getSubtitle: (item: T) => string;
    /** Function to get the item's price display */
    getPrice: (item: T) => string;
    /** Optional: Function to get the full title (for tooltip when truncated) */
    getTitleFull?: (item: T) => string;
    /** Optional: Function to get the full subtitle (for tooltip when truncated) */
    getSubtitleFull?: (item: T) => string;
    /** Optional: Returns the numeric price used for e.g. DiscountField maxAmount */
    getNumericPrice?: (item: T) => number;
}

export interface ItemSelectionDrawerProps<T> {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Drawer title displayed in header */
    title: string;
    /** All available items to display */
    items: T[];
    /** Currently selected items (committed state) */
    selectedItems: T[];
    /** Temporary selected items (staged state) */
    tempSelectedItems: T[];
    /** Search query for filtering */
    searchQuery: string;
    /** Filtered items based on search */
    filteredItems: T[];
    /** Label for "Select All" checkbox (e.g., "All Services", "All Products") */
    selectAllLabel: string;
    /** Search input placeholder text */
    searchPlaceholder: string;
    /** Configuration for displaying item details */
    itemDisplay: ItemDisplayConfig<T>;
    /**
     * Optional slot that replaces the price display for each item.
     * Receives the item and whether it is currently checked (temp selection).
     * Use this to render custom per-item actions (e.g. a discount input).
     * When provided, the static price text is NOT rendered.
     */
    renderItemAction?: (item: T, isChecked: boolean) => React.ReactNode;
    /** Whether the drawer is in read-only mode (view mode) */
    disabled?: boolean;
    /** Callback when drawer closes */
    onClose: () => void;
    /** Callback when search query changes */
    onSearchChange: (query: string) => void;
    /** Callback when temp selection changes */
    onTempSelectionChange: (items: T[]) => void;
    /** Callback when Apply button is clicked */
    onApply: () => void;
    /** Callback when Cancel button is clicked */
    onCancel: () => void;
    /** Label for the confirm button. Defaults to "Apply" */
    applyLabel?: string;
    /** Visual variant. `"forms"` matches the Select Forms Figma. */
    variant?: 'default' | 'forms';
}

export const ItemSelectionDrawer = <T extends { id?: string | number }>({
    isOpen,
    title,
    items,
    selectedItems,
    tempSelectedItems,
    searchQuery,
    filteredItems,
    selectAllLabel,
    searchPlaceholder,
    itemDisplay,
    renderItemAction,
    disabled = false,
    onClose,
    onSearchChange,
    onTempSelectionChange,
    onApply,
    onCancel,
    applyLabel = 'Apply',
    variant = 'default'
}: ItemSelectionDrawerProps<T>): React.ReactElement | null => {
    const isSameId = (left?: string | number, right?: string | number) => String(left) === String(right);

    const handleSelectAll = React.useCallback(
        (checked: boolean) => {
            if (disabled) return;
            if (checked) {
                onTempSelectionChange(filteredItems);
            } else {
                onTempSelectionChange([]);
            }
        },
        [filteredItems, onTempSelectionChange, disabled]
    );

    const handleItemToggle = React.useCallback(
        (item: T) => {
            if (disabled) return;
            const isSelected = tempSelectedItems.some(selected => isSameId(selected.id, item.id));
            if (isSelected) {
                onTempSelectionChange(tempSelectedItems.filter(selected => !isSameId(selected.id, item.id)));
            } else {
                onTempSelectionChange([...tempSelectedItems, item]);
            }
        },
        [tempSelectedItems, onTempSelectionChange, disabled]
    );

    const isItemChecked = React.useCallback(
        (item: T): boolean => {
            return tempSelectedItems.some(selected => isSameId(selected.id, item.id));
        },
        [tempSelectedItems]
    );

    if (!isOpen) {
        return null;
    }

    return (
        <Drawer onClose={onClose}>
            <DrawerHeader
                title={title}
                alwaysShowClose={variant === 'forms'}
                className={variant === 'forms' ? styles.formsHeader : undefined}
            />

            <DrawerBody className={variant === 'forms' ? styles.formsBody : undefined}>
                <Input
                    placeholder={searchPlaceholder}
                    startIcon={<FontAwesomeIcon icon={faSearch} />}
                    value={searchQuery}
                    onChange={event => onSearchChange(event.target.value)}
                    disabled={disabled}
                />

                <div className={clsx(styles.row, variant === 'forms' && styles.forms)}>
                    <div className={styles.toggleAll}>
                        {variant === 'forms' ? (
                            <Checkbox
                                label={selectAllLabel}
                                onChange={disabled ? undefined : handleSelectAll}
                                checked={filteredItems.length > 0 && filteredItems.every(item => isItemChecked(item))}
                                iconSize={15}
                                className={clsx(styles.selectAllCheckbox, { [styles.disabled]: disabled })}
                            />
                        ) : (
                            <>
                                <div className={clsx(styles.checkboxWrapper, { [styles.disabled]: disabled })}>
                                    <Checkbox
                                        onChange={disabled ? undefined : handleSelectAll}
                                        checked={
                                            filteredItems.length > 0 && filteredItems.every(item => isItemChecked(item))
                                        }
                                    />
                                </div>
                                <Label>{selectAllLabel}</Label>
                            </>
                        )}
                    </div>
                    {filteredItems.map(item => {
                        const title = itemDisplay.getTitle(item);
                        const subtitle = itemDisplay.getSubtitle(item);
                        const titleFull = itemDisplay.getTitleFull?.(item);
                        const subtitleFull = itemDisplay.getSubtitleFull?.(item);

                        return (
                            <div
                                key={item.id}
                                className={clsx(styles.selectableItem, { [styles.disabled]: disabled })}
                                onClick={disabled ? undefined : () => handleItemToggle(item)}
                            >
                                <div className={clsx(styles.checkboxWrapper, { [styles.disabled]: disabled })}>
                                    <Checkbox
                                        checked={isItemChecked(item)}
                                        iconSize={variant === 'forms' ? 15 : undefined}
                                        className={styles.checkbox}
                                    />
                                </div>
                                <div
                                    className={styles.meta}
                                    onClick={renderItemAction ? e => e.stopPropagation() : undefined}
                                >
                                    <h6>
                                        {titleFull && titleFull !== title ? (
                                            <Tooltip
                                                content={titleFull}
                                                placement="top"
                                                className={styles.customTooltip}
                                            >
                                                <span className={styles.tooltipText}>{title}</span>
                                            </Tooltip>
                                        ) : (
                                            title
                                        )}
                                    </h6>
                                    {subtitle && (
                                        <p>
                                            {subtitleFull && subtitleFull !== subtitle ? (
                                                <Tooltip
                                                    content={subtitleFull}
                                                    placement="top"
                                                    className={styles.customTooltip}
                                                >
                                                    <span className={styles.tooltipText}>{subtitle}</span>
                                                </Tooltip>
                                            ) : (
                                                subtitle
                                            )}
                                        </p>
                                    )}
                                </div>
                                {renderItemAction ? (
                                    <div
                                        className={styles.itemAction}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {renderItemAction(item, isItemChecked(item))}
                                    </div>
                                ) : (
                                    itemDisplay.getPrice(item) && (
                                        <p className={styles.price}>{itemDisplay.getPrice(item)}</p>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>
            </DrawerBody>

            <DrawerFooter>
                {disabled ? (
                    <Button
                        size="large"
                        label="Close"
                        variant="black"
                        onClick={onClose}
                    />
                ) : (
                    <>
                        <Button
                            size="large"
                            label="Cancel"
                            variant="black"
                            onClick={onCancel}
                        />
                        <Button
                            size="large"
                            label={applyLabel}
                            variant="green"
                            onClick={onApply}
                        />
                    </>
                )}
            </DrawerFooter>
        </Drawer>
    );
};
